/**
 * SEO Utilities for Neumind Website
 * Provides hooks and utilities for meta tags, Open Graph, and JSON-LD structured data.
 */

import { useEffect } from 'react';
import { FAQItem } from './faqs';

// Types
export interface PageSEOConfig {
    title: string;
    description?: string;
    canonical?: string;
    ogType?: 'website' | 'article' | 'product';
    ogImage?: string;
    noIndex?: boolean;
    article?: {
        publishedTime?: string;
        author?: string;
        tags?: string[];
    };
}

export interface BreadcrumbItem {
    name: string;
    url: string;
}

export interface ProductOffer {
    name: string;
    description: string;
    price: number;
    priceCurrency: string;
    availability: 'InStock' | 'OutOfStock' | 'PreOrder';
    url: string;
}

export interface BlogPostSchema {
    title: string;
    description: string;
    datePublished: string;
    dateModified?: string;
    author: string;
    authorRole?: string;
    image?: string;
    url: string;
    tags?: string[];
}

const BASE_URL = 'https://www.neumind.io';
const DEFAULT_IMAGE = '/images/og/default.png';
const SITE_NAME = 'Neumind';

// Helper to set or remove a meta tag
function setMetaTag(name: string, content: string | undefined, isProperty = false) {
    const attr = isProperty ? 'property' : 'name';
    let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;

    if (content) {
        if (!element) {
            element = document.createElement('meta');
            element.setAttribute(attr, name);
            document.head.appendChild(element);
        }
        element.content = content;
    } else if (element) {
        element.remove();
    }
}

// Helper to set or remove JSON-LD script
function setJsonLd(id: string, data: object | null) {
    let script = document.getElementById(id) as HTMLScriptElement | null;

    if (data) {
        if (!script) {
            script = document.createElement('script');
            script.id = id;
            script.type = 'application/ld+json';
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(data);
    } else if (script) {
        script.remove();
    }
}

// Hook to set page-level SEO meta tags
export function useSEO(config: PageSEOConfig) {
    useEffect(() => {
        const { title, description, canonical, ogType = 'website', ogImage, noIndex, article } = config;
        const fullTitle = title === 'Neumind' ? title : `${title} | Neumind`;
        const fullCanonical = canonical ? `${BASE_URL}${canonical}` : undefined;
        const fullImage = ogImage?.startsWith('http') ? ogImage : `${BASE_URL}${ogImage || DEFAULT_IMAGE}`;

        // Title
        document.title = fullTitle;

        // Basic meta tags
        setMetaTag('description', description);
        setMetaTag('robots', noIndex ? 'noindex, nofollow' : undefined);

        // Canonical
        let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (fullCanonical) {
            if (!linkCanonical) {
                linkCanonical = document.createElement('link');
                linkCanonical.rel = 'canonical';
                document.head.appendChild(linkCanonical);
            }
            linkCanonical.href = fullCanonical;
        } else if (linkCanonical) {
            linkCanonical.remove();
        }

        // Open Graph
        setMetaTag('og:title', fullTitle, true);
        setMetaTag('og:description', description, true);
        setMetaTag('og:type', ogType, true);
        setMetaTag('og:url', fullCanonical, true);
        setMetaTag('og:image', fullImage, true);
        setMetaTag('og:site_name', SITE_NAME, true);

        // Twitter Card
        setMetaTag('twitter:card', 'summary_large_image');
        setMetaTag('twitter:title', fullTitle);
        setMetaTag('twitter:description', description);
        setMetaTag('twitter:image', fullImage);

        // Clear any stale article meta from a previous page
        setMetaTag('article:published_time', undefined, true);
        setMetaTag('article:author', undefined, true);
        // Remove leftover article:tag:N meta elements
        document.querySelectorAll('meta[property^="article:tag:"]').forEach(el => el.remove());

        // Article-specific meta
        if (article) {
            setMetaTag('article:published_time', article.publishedTime, true);
            setMetaTag('article:author', article.author, true);
            article.tags?.forEach((tag, i) => {
                setMetaTag(`article:tag:${i}`, tag, true);
            });
        }
    }, [config]);
}

// Hook to set BreadcrumbList JSON-LD schema
export function useBreadcrumbSchema(items: BreadcrumbItem[]) {
    useEffect(() => {
        if (!items.length) return;

        const schema = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: items.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.name,
                item: `${BASE_URL}${item.url}`,
            })),
        };

        setJsonLd('breadcrumb-schema', schema);

        return () => {
            setJsonLd('breadcrumb-schema', null);
        };
    }, [items]);
}

// Hook to set FAQPage JSON-LD schema
export function useFAQSchema(faqs: FAQItem[]) {
    useEffect(() => {
        if (!faqs.length) return;

        const schema = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                },
            })),
        };

        setJsonLd('faq-schema', schema);

        return () => {
            setJsonLd('faq-schema', null);
        };
    }, [faqs]);
}

// Hook to set Product/Offer JSON-LD schema
export function useProductSchema(products: ProductOffer[]) {
    useEffect(() => {
        if (!products.length) return;

        const schema = {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: products.map((product, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: {
                    '@type': 'Product',
                    name: product.name,
                    description: product.description,
                    url: `${BASE_URL}${product.url}`,
                    offers: {
                        '@type': 'Offer',
                        price: product.price,
                        priceCurrency: product.priceCurrency,
                        availability: `https://schema.org/${product.availability}`,
                    },
                },
            })),
        };

        setJsonLd('product-schema', schema);

        return () => {
            setJsonLd('product-schema', null);
        };
    }, [products]);
}

// Hook to set BlogPosting JSON-LD schema
export function useBlogPostSchema(schema: BlogPostSchema | null) {
    useEffect(() => {
        if (!schema) return;

        const jsonLd = {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: schema.title,
            description: schema.description,
            datePublished: schema.datePublished,
            dateModified: schema.dateModified || schema.datePublished,
            author: {
                '@type': 'Person',
                name: schema.author,
                ...(schema.authorRole && { jobTitle: schema.authorRole }),
            },
            publisher: {
                '@type': 'Organization',
                name: 'Neumind',
                logo: {
                    '@type': 'ImageObject',
                    url: `${BASE_URL}/images/logo.png`,
                },
            },
            ...(schema.image && {
                image: {
                    '@type': 'ImageObject',
                    url: schema.image.startsWith('http') ? schema.image : `${BASE_URL}${schema.image}`,
                },
            }),
            mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': schema.url,
            },
            ...(schema.tags?.length && { keywords: schema.tags.join(', ') }),
        };

        setJsonLd('blogpost-schema', jsonLd);

        return () => {
            setJsonLd('blogpost-schema', null);
        };
    }, [schema]);
}

// Page-specific SEO configurations
export const PageSEO: Record<string, PageSEOConfig> = {
    home: {
        title: 'Neumind',
        description: 'AI-powered cognitive rehabilitation platform for brain injury and stroke survivors. Personalized daily support, clinician connectivity, and evidence-based recovery tools.',
        canonical: '/',
    },
    pricing: {
        title: 'Pricing',
        description: 'Transparent pricing for Neumind cognitive rehabilitation plans. Options for individuals, clinicians, and case managers with flexible billing.',
        canonical: '/pricing',
        ogType: 'product',
    },
    about: {
        title: 'About Us',
        description: 'Learn about Neumind\'s mission to transform cognitive rehabilitation through technology. Founded by brain injury survivors and rehabilitation experts.',
        canonical: '/about',
    },
    contact: {
        title: 'Contact Us',
        description: 'Get in touch with the Neumind team. Questions about our cognitive rehabilitation platform, partnership opportunities, or support inquiries.',
        canonical: '/contact-us',
    },
    trust: {
        title: 'Trust & Compliance',
        description: "Neumind's compliance posture, our public legal stack, and how to request the full DTAC pack. UK GDPR, DCB0129, DTAC v2.",
        canonical: '/trust',
    },
    privacy: {
        title: 'Privacy Notice',
        description: 'How Neumind collects, processes, and protects personal data across our website, app, and professional dashboard. Compliant with UK GDPR, DPA 2018, and PECR.',
        canonical: '/privacy',
    },
    cookies: {
        title: 'Cookies Notice',
        description: 'Neumind cookies notice — what cookies and similar technologies we set, the lawful basis, and how to manage them.',
        canonical: '/cookies',
    },
    eula: {
        title: 'App EULA',
        description: 'Neumind App End User Licence Agreement — terms governing your use of the Neumind mobile application.',
        canonical: '/legal/eula',
    },
    platformEula: {
        title: 'Platform EULA',
        description: 'Neumind Platform End-User Licence Agreement — terms governing professional use of the Neumind Platform by clinicians, therapists and rehabilitation professionals.',
        canonical: '/legal/platform-eula',
    },
    dpa: {
        title: 'Data Processing Agreement',
        description: 'Neumind Data Processing Agreement (Processor Terms) — governs how Neumind processes personal data on behalf of Customer organisations.',
        canonical: '/legal/dpa',
    },
    subprocessors: {
        title: 'Sub-processors',
        description: "Neumind sub-processors register — every third party that processes personal data on Neumind's behalf, with purpose, region, and transfer mechanism.",
        canonical: '/legal/subprocessors',
    },
    intendedPurpose: {
        title: 'Intended Purpose Statement',
        description: "Neumind intended purpose statement — what Neumind is, who it's for, and how it sits within the UK regulatory framework for digital health products.",
        canonical: '/legal/intended-purpose',
    },
    blog: {
        title: 'Blog',
        description: 'Insights, updates, and practical articles on brain injury rehabilitation, stroke recovery, and cognitive health from the Neumind team.',
        canonical: '/resources/blog',
    },
    research: {
        title: 'Research & Evidence',
        description: 'The clinical evidence and research underpinning Neumind\'s approach to cognitive rehabilitation — microprompting, external memory aids, and INCOG 2023 guidelines.',
        canonical: '/resources/research',
    },
    caseStudies: {
        title: 'Case Studies',
        description: 'Real-world implementation stories from clinicians and survivors using Neumind for cognitive rehabilitation.',
        canonical: '/resources/case-studies',
    },
    resources: {
        title: 'Resources',
        description: 'Guides, research, and stories on brain injury and stroke rehabilitation from the Neumind team.',
        canonical: '/resources',
    },
    solutionsClients: {
        title: 'For Individuals & Families',
        description: 'Neumind helps brain injury survivors regain independence with personalized prompts, mood tracking, and family connectivity.',
        canonical: '/solutions/clients',
    },
    solutionsProfessionals: {
        title: 'For Professionals',
        description: 'Streamline your rehabilitation practice with Neumind\'s clinician dashboard, remote monitoring, and AI-assisted therapy planning.',
        canonical: '/solutions/professionals',
    },
    consulting: {
        title: 'Consulting & Implementation',
        description: 'Expert assistive technology implementation services. From Neumind setup to bespoke smart-home integration for brain injury rehabilitation.',
        canonical: '/solutions/consulting',
    },
};
